<template>
  <!-- Chỉ số AI nhận diện của 1 tin. Dùng chung cho panel chi tiết và modal tổng quan.
       Mặc định chỉ xem: tick dòng đúng rồi Lưu ngay, Sửa khi AI đọc sai, Bỏ qua / Xoá khi không cần -->
  <div class="kpi-review" :class="{ 'is-editing': editing }">
    <div class="kpi-review__head" :class="{ clickable: collapsible }" @click="collapsible && (collapsed = !collapsed)">
      <span class="kpi-review__title">{{ title }}</span>
      <span v-if="view.kpiStatus === 1" class="kpi-review__state kpi-review__state--saved">
        ✓ Đã lưu · {{ formatTime(view.kpiSavedAt) }}
      </span>
      <span v-else-if="view.kpiStatus === 2" class="kpi-review__state kpi-review__state--skipped">
        Đã bỏ qua · {{ formatTime(view.kpiSavedAt) }}
      </span>
      <span v-else-if="editing" class="kpi-review__state">Đang sửa</span>
      <span v-else class="kpi-review__state kpi-review__state--pending">Chờ lưu</span>
      <svg v-if="collapsible" class="kpi-review__chevron" :class="{ open: !collapsed }" width="14" height="14"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </div>

    <template v-if="!collapsed">
      <table v-if="view.glucoseRows" class="kpi-review__table">
        <thead>
          <tr>
            <th v-if="!readonly"></th>
            <th>Giá trị</th>
            <th>Đơn vị</th>
            <th>Khung giờ</th>
            <th>Thời điểm đo</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in rows" :key="i" :class="{ 'is-off': !row.checked }">
            <td v-if="!readonly" data-label="Lưu dòng này"><b-form-checkbox v-model="row.checked" :disabled="locked" /></td>
            <td data-label="Giá trị">
              <b-form-input v-if="canEdit" v-model="row.value" class="kpi-review__input kpi-review__input--num" />
              <span v-else class="kpi-review__text">{{ row.value }}</span>
            </td>
            <td data-label="Đơn vị">{{ row.unitType === 1 ? 'mg/dL' : 'mmol/L' }}</td>
            <td data-label="Khung giờ">
              <b-form-select v-if="canEdit" v-model="row.timeFrameId" size="sm" class="kpi-review__input kpi-review__input--auto">
                <b-form-select-option v-for="tf in glucoseTimeFrames" :key="tf.id" :value="tf.id">{{ tf.label }}</b-form-select-option>
              </b-form-select>
              <span v-else class="kpi-review__text">{{ timeFrameLabel(row.timeFrameId) }}</span>
            </td>
            <td data-label="Thời điểm đo">
              <div v-if="canEdit" class="kpi-review__date">
                <b-form-input
                  type="datetime-local"
                  class="kpi-review__input kpi-review__input--auto"
                  :value="toInputValue(row.createDate)"
                  @input="row.createDate = fromInputValue($event)"
                />
                <button type="button" class="kpi-review__link" @click="row.createDate = nowString()">Bây giờ</button>
                <button v-if="row.createDate" type="button" class="kpi-review__link" @click="row.createDate = ''" title="Để trống: lấy lúc nhận phân tích">Xoá</button>
              </div>
              <span v-else class="kpi-review__text" :class="{ muted: !row.createDate }">
                {{ row.createDate || 'Lúc nhận phân tích' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <template v-else-if="view.nutrition">
        <div class="kpi-review__meta">
          <span>Bữa</span>
          <b-form-select v-if="canEdit" v-model="mealTimeFrameId" size="sm" class="kpi-review__input kpi-review__input--auto">
            <b-form-select-option v-for="tf in mealTimeFrames" :key="tf.id" :value="tf.id">{{ tf.label }}</b-form-select-option>
          </b-form-select>
          <strong v-else>{{ mealLabel(mealTimeFrameId) }}</strong>
          <span v-if="view.nutrition.totalMealScore != null">· Điểm bữa ăn: {{ view.nutrition.totalMealScore }}</span>
        </div>
        <table class="kpi-review__table">
          <thead>
            <tr>
              <th v-if="!readonly"></th>
              <th>Món</th>
              <th>Khẩu phần</th>
              <th>Đơn vị</th>
              <th>Calo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in items" :key="i" :class="{ 'is-off': !item.checked }">
              <td v-if="!readonly" data-label="Lưu món này"><b-form-checkbox v-model="item.checked" :disabled="locked" /></td>
              <td data-label="Món">
                <b-form-input v-if="canEdit" v-model="item.name" class="kpi-review__input" />
                <span v-else class="kpi-review__text">{{ item.name }}</span>
              </td>
              <td data-label="Khẩu phần">
                <b-form-input v-if="canEdit" v-model.number="item.portion" type="number" step="any" class="kpi-review__input kpi-review__input--num" @input="onPortionInput(item)" />
                <span v-else class="kpi-review__text">{{ item.portion }}</span>
              </td>
              <td data-label="Đơn vị">
                <b-form-input v-if="canEdit" v-model="item.unit" class="kpi-review__input kpi-review__input--num" />
                <span v-else class="kpi-review__text">{{ item.unit }}</span>
              </td>
              <td data-label="Calo">
                <b-form-input v-if="canEdit" v-model.number="item.calorie" type="number" step="any" class="kpi-review__input kpi-review__input--num" @input="onCalorieInput(item)" />
                <span v-else class="kpi-review__text">{{ item.calorie }}</span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="kpi-review__total">
              <td :colspan="readonly ? 3 : 4">Tổng calo{{ readonly ? '' : ' (món được chọn)' }}</td>
              <td data-label="Tổng calo">{{ totalCalories }}</td>
            </tr>
          </tfoot>
        </table>
      </template>

      <!-- Modal tổng quan: ở đây chỉ xác nhận sửa, lưu sang app bằng nút Lưu trên dòng -->
      <div class="kpi-review__actions" v-if="draftOnly && canEdit">
        <button class="btn btn-success btn-sm" @click="saveDraft" :disabled="busy || checkedCount === 0">
          <span v-if="saving" class="btn-spinner"></span>
          {{ saving ? 'Đang lưu...' : 'Lưu thay đổi' }}
        </button>
      </div>
      <div class="kpi-review__actions" v-else-if="!draftOnly && !readonly && (!locked || canDelete)">
        <button v-if="canDelete" class="btn btn-outline-danger btn-sm mr-auto" @click="remove" :disabled="busy">
          <span v-if="deleting" class="btn-spinner"></span>
          Xoá
        </button>
        <template v-if="!locked">
          <button class="btn btn-outline-secondary btn-sm" @click="editing = !editing" :disabled="busy">
            {{ editing ? 'Xong sửa' : 'Sửa' }}
          </button>
          <button class="btn btn-skip btn-sm ml-2" @click="skip" :disabled="busy">
            <span v-if="skipping" class="btn-spinner"></span>
            {{ skipping ? 'Đang xử lý...' : 'Bỏ qua' }}
          </button>
          <button class="btn btn-success btn-sm ml-2" @click="save" :disabled="busy || checkedCount === 0">
            <span v-if="saving" class="btn-spinner"></span>
            {{ saving ? 'Đang lưu...' : `Lưu chỉ số (${checkedCount})` }}
          </button>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import decisionAiCoachService from '../services/decisionAiCoachService';
import { GLUCOSE_TIME_FRAMES, MEAL_TIME_FRAMES, DEFAULT_MEAL_TIME_FRAME } from '../constants/kpiTimeFrames';

const pad = n => String(n).padStart(2, '0');
// Các trường dinh dưỡng của 1 món, tính theo cả món (AI trả)
const NUTRIENT_FIELDS = ['calorie', 'glucose', 'lipid', 'protein', 'fibre'];

export default {
  name: 'KpiEditor',
  props: {
    // KpiView từ BE: { id, kpiType, kpiStatus, kpiSavedAt, glucoseRows | nutrition }
    view: { type: Object, required: true },
    title: { type: String, default: 'Chỉ số AI nhận diện' },
    startEditing: { type: Boolean, default: false },
    allowDelete: { type: Boolean, default: false },
    collapsible: { type: Boolean, default: false },
    // Chỉ hiển thị: không tick, không nút - dùng cho nút Xem ở modal (thao tác đã nằm trên dòng)
    readonly: { type: Boolean, default: false },
    // Chỉ lưu thay đổi vào tin (không lưu sang app) - dùng ở modal, nơi đã có nút Lưu trên dòng
    draftOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      rows: [],
      items: [],
      mealTimeFrameId: null,
      editing: false,
      collapsed: false,
      saving: false,
      skipping: false,
      deleting: false,
      glucoseTimeFrames: GLUCOSE_TIME_FRAMES,
      mealTimeFrames: MEAL_TIME_FRAMES,
    };
  },
  computed: {
    // Đã lưu (1) hoặc bỏ qua (2) thì khoá
    locked() {
      return this.view.kpiStatus !== 0;
    },
    canEdit() {
      return this.editing && !this.locked && !this.readonly;
    },
    // Đã lưu thì số nằm trong dữ liệu sức khỏe rồi, xoá ở đây không gỡ được - BE cũng chặn
    canDelete() {
      return this.allowDelete && this.view.kpiStatus !== 1;
    },
    busy() {
      return this.saving || this.skipping || this.deleting;
    },
    totalCalories() {
      const total = this.items.filter(x => this.readonly || x.checked).reduce((sum, x) => sum + (Number(x.calorie) || 0), 0);
      return Math.round(total * 10) / 10;
    },
    checkedCount() {
      return (this.view.nutrition ? this.items : this.rows).filter(x => x.checked).length;
    },
  },
  watch: {
    // Đổi sang tin khác thì dựng lại bản sao; chỉ đổi trạng thái (sau khi lưu) thì giữ nguyên dòng đã sửa
    'view.id': {
      immediate: true,
      handler() {
        this.init();
      },
    },
    // Báo bản đang sửa (món được tick) để dòng ngoài cập nhật Món / Calo ngay khi gõ
    items: {
      deep: true,
      handler(items) {
        if (this.view.nutrition) this.$emit('draft', { id: this.view.id, items: items.filter(x => x.checked).map(this.strip) });
      },
    },
  },
  // Đóng editor thì bản sửa chưa lưu mất theo -> dòng ngoài quay về số cũ
  beforeDestroy() {
    if (this.view.nutrition) this.$emit('draft', { id: this.view.id, items: null });
  },
  methods: {
    init() {
      const v = this.view;
      this.rows = (v.glucoseRows || []).map(r => ({ ...r, checked: true }));
      this.items = ((v.nutrition && v.nutrition.items) || []).map(x => ({ ...x, checked: true, perUnit: this.perUnitOf(x) }));
      this.mealTimeFrameId = (v.nutrition && v.nutrition.timeFrameId) || DEFAULT_MEAL_TIME_FRAME;
      this.editing = this.startEditing && v.kpiStatus === 0;
    },
    timeFrameLabel(id) {
      return (GLUCOSE_TIME_FRAMES.find(t => t.id === id) || {}).label || '—';
    },
    mealLabel(id) {
      return (MEAL_TIME_FRAMES.find(t => t.id === id) || {}).label || '—';
    },
    // BE trả giờ UTC: số giây unix hoặc chuỗi không có "Z"
    formatTime(value) {
      if (!value) return '';
      const d = typeof value === 'number'
        ? new Date(value < 10000000000 ? value * 1000 : value)
        : new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`);
      if (isNaN(d)) return '';
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    // Giờ trên máy coach = giờ VN, gửi đúng định dạng BE đọc ("yyyy-MM-dd HH:mm:ss")
    nowString() {
      const d = new Date();
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    },
    // "yyyy-MM-dd HH:mm:ss" <-> "yyyy-MM-ddTHH:mm" của input datetime-local
    toInputValue(s) {
      if (!s) return '';
      const m = String(s).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
      return m ? `${m[1]}T${m[2]}` : '';
    },
    fromInputValue(v) {
      return v ? `${v.replace('T', ' ')}:00` : '';
    },
    // Calo / chất của AI là cho cả món -> giữ mức trên 1 đơn vị khẩu phần để đổi khẩu phần thì tính lại
    perUnitOf(item) {
      const portion = Number(item.portion);
      if (!(portion > 0)) return null;
      return NUTRIENT_FIELDS.reduce((acc, f) => ({ ...acc, [f]: (Number(item[f]) || 0) / portion }), {});
    },
    onPortionInput(item) {
      const portion = Number(item.portion);
      if (!item.perUnit || !(portion > 0)) return;
      NUTRIENT_FIELDS.forEach(f => {
        item[f] = Math.round(item.perUnit[f] * portion * 10) / 10;
      });
    },
    // Coach sửa thẳng calo thì lấy đó làm mức mới trên 1 đơn vị
    onCalorieInput(item) {
      const portion = Number(item.portion);
      if (!(portion > 0)) return;
      item.perUnit = { ...(item.perUnit || this.perUnitOf(item)), calorie: (Number(item.calorie) || 0) / portion };
    },
    strip(x) {
      const copy = { ...x };
      delete copy.checked;
      delete copy.perUnit;
      return copy;
    },
    notify(body, okDefault, failDefault) {
      if (body.success) this.$toastr.s({ title: 'Thành công', msg: body.message || okDefault });
      else this.$toastr.e({ title: 'Lỗi', msg: body.message || body.error || failDefault });
      return !!body.success;
    },
    buildPayload() {
      return this.view.nutrition
        ? {
            nutrition: {
              ...this.view.nutrition,
              timeFrameId: this.mealTimeFrameId,
              items: this.items.filter(x => x.checked).map(this.strip),
            },
          }
        : { glucoseRows: this.rows.filter(r => r.checked).map(this.strip) };
    },
    async saveDraft() {
      if (this.busy) return;
      const payload = this.buildPayload();
      this.saving = true;
      try {
        const body = (await decisionAiCoachService.updateKpi(this.view.id, payload)) || {};
        if (this.notify(body, 'Đã lưu thay đổi', 'Lưu thay đổi thất bại')) {
          this.editing = false;
          this.$emit('updated', { id: this.view.id, ...payload });
        }
      } catch (e) {
        console.error('[KpiEditor.saveDraft]', e);
        this.$toastr.e({ title: 'Lỗi', msg: 'Lưu thay đổi thất bại, vui lòng thử lại' });
      } finally {
        this.saving = false;
      }
    },
    async save() {
      if (this.busy) return;
      const payload = this.buildPayload();
      this.saving = true;
      try {
        const body = (await decisionAiCoachService.saveKpi(this.view.id, payload)) || {};
        if (this.notify(body, 'Đã lưu chỉ số', 'Lưu chỉ số thất bại')) {
          this.editing = false;
          this.$emit('handled', { id: this.view.id, status: 1, nutrition: payload.nutrition });
        }
      } catch (e) {
        console.error('[KpiEditor.save]', e);
        this.$toastr.e({ title: 'Lỗi', msg: 'Lưu chỉ số thất bại, vui lòng thử lại' });
      } finally {
        this.saving = false;
      }
    },
    async skip() {
      if (this.busy) return;
      this.skipping = true;
      try {
        const body = (await decisionAiCoachService.skipKpi(this.view.id)) || {};
        if (this.notify(body, 'Đã bỏ qua chỉ số', 'Bỏ qua chỉ số thất bại')) {
          this.editing = false;
          this.$emit('handled', { id: this.view.id, status: 2 });
        }
      } catch (e) {
        console.error('[KpiEditor.skip]', e);
        this.$toastr.e({ title: 'Lỗi', msg: 'Bỏ qua chỉ số thất bại, vui lòng thử lại' });
      } finally {
        this.skipping = false;
      }
    },
    async remove() {
      if (this.busy) return;
      const ok = await this.$bvModal.msgBoxConfirm('Xoá chỉ số AI nhận diện của tin này? Tin nhắn vẫn giữ nguyên.', {
        title: 'Xoá chỉ số',
        okTitle: 'Xoá',
        okVariant: 'danger',
        cancelTitle: 'Huỷ',
        centered: true,
      });
      if (!ok) return;

      this.deleting = true;
      try {
        const body = (await decisionAiCoachService.deleteKpi(this.view.id)) || {};
        // status null = tin không còn chỉ số
        if (this.notify(body, 'Đã xoá chỉ số', 'Xoá chỉ số thất bại')) {
          this.$emit('handled', { id: this.view.id, status: null });
        }
      } catch (e) {
        console.error('[KpiEditor.remove]', e);
        this.$toastr.e({ title: 'Lỗi', msg: 'Xoá chỉ số thất bại, vui lòng thử lại' });
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.kpi-review {
  overflow-x: auto; // bảng 5 cột trên màn hình điện thoại
  padding: 8px 10px;
  background: #F4FBF8;
  border-left: 3px solid #1D9E75;
  border-radius: 4px;

  &.is-editing {
    border-left-color: #BA7517;
  }
}

.kpi-review__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;

  &.clickable {
    cursor: pointer;
    user-select: none;
  }
}

.kpi-review__title {
  font-size: 10px;
  color: #0F6E56;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi-review__state {
  margin-left: auto;
  font-size: 11px;
  color: #854F0B;

  &--saved { color: #0F6E56; }
  &--skipped { color: #888780; }
  &--pending { color: #854F0B; }
}

.kpi-review__chevron {
  flex-shrink: 0;
  color: #888;
  transition: transform 0.2s;

  &.open { transform: rotate(180deg); }
}

.kpi-review__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #333;
  margin-bottom: 6px;
}

.kpi-review__total td {
  font-weight: 600;
  border-top: 1px solid #d9e8e1;
}

.kpi-review__table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;

  th {
    font-weight: 500;
    color: #777;
    text-align: left;
    padding: 3px 6px;
    white-space: nowrap;
  }

  td {
    padding: 4px 6px;
    border-top: 1px solid #E3EFE9;
    vertical-align: middle;
  }

  tr.is-off td {
    opacity: 0.45;
  }
}

.kpi-review__text {
  color: #222;

  &.muted { color: #999; font-style: italic; }
}

.kpi-review__input {
  width: 100%;
  font-size: 12px;
  padding: 2px 4px;
  border: 1px solid #D3E5DC;
  border-radius: 3px;
  background: #fff;

  &--num { max-width: 80px; }
  &--auto { width: auto; }
}

.kpi-review__date {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.kpi-review__link {
  border: none;
  background: none;
  padding: 0;
  font-size: 11px;
  color: #185FA5;
  cursor: pointer;

  &:hover { text-decoration: underline; }
}

.kpi-review__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
}

// Mobile: bảng -> mỗi dòng là 1 thẻ "nhãn: giá trị", ô nhập đủ lớn để chạm, nút dàn đều 1 hàng
@media (max-width: 575px) {
  .kpi-review {
    overflow-x: visible;
  }

  .kpi-review__table {
    thead { display: none; }

    tr {
      display: block;
      padding: 6px 0;
      border-top: 1px solid #E3EFE9;
    }

    td {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 3px 0;
      border-top: none;
      min-height: 32px;
    }

    .kpi-review__total td:first-child {
      display: none;
    }

    td::before {
      content: attr(data-label);
      flex-shrink: 0;
      font-size: 11px;
      color: #777;
    }
  }

  .kpi-review__input {
    width: auto;
    flex: 1;
    max-width: 65%;
    min-height: 32px;
    font-size: 14px; // < 16px thì iOS tự zoom khi focus, 14px là mức chấp nhận được với ô nhỏ

    &--num { max-width: 65%; }
  }

  .kpi-review__date {
    flex: 1;
    justify-content: flex-end;

    .kpi-review__input { max-width: 100%; }
  }

  .kpi-review__link {
    padding: 6px 4px;
    font-size: 12px;
  }

  .kpi-review__actions {
    flex-wrap: wrap;
    gap: 6px;

    .btn {
      flex: 1 1 0;
      min-height: 36px;
      margin-left: 0 !important;
    }

    // Xoá tách riêng 1 hàng cho khỏi bấm nhầm cạnh nút Lưu
    .btn.mr-auto {
      flex-basis: 100%;
      order: 2;
    }
  }
}
</style>
